import React, { useState, useEffect, useContext, Fragment } from "react";
import {
  Button,
  message,
  Row,
  Col,
  Typography,
  Descriptions,
  Skeleton,
  List,
  Table,
} from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import {
  getPaymentFailed,
  getPaymentSuccess,
  getPaymentURL,
  getMySubscription,
} from "../../../../services/payment.services";
import { ApplicationList } from "../../../../services/applications.services";
import ResultWizz from "../../../../blocks/ResultWizz/ResultWizz";
import { useQuery } from "../../../../helpers/utility";
import { SelectedVendorDetailsContext } from "../../../../context/SelectedVendorContext";
import CardWizz from "../../../../blocks/Card/Card";
import OfflinePayment from "./OfflinePayment";
import moment from "moment";
import { useHistory } from "react-router-dom";

const { Title } = Typography;

const GetCerfied = () => {
  const { vendorDetails, setVendorDetails } = useContext(
    SelectedVendorDetailsContext
  );
  const vendorId = vendorDetails?.id;
  const history = useHistory();
  const query = useQuery();
  const paymentStatus = query.get("status");
  const sessionId = query.get("session_id");
  const applicationId = query.get("application_id");

  const [subscriptionData, setSubscriptionData] = useState([]);
  const [transactionId, setTransactionId] = useState(null);
  const [formType, setFormType] = useState(null);
  const [loadingPaymentStatus, setLoadingPaymentStatus] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  // Backend CreatePageToken requires the application_id (`pqq`); without it,
  // the Stripe session creation 400s and the user is stuck. The page used to
  // call getPaymentURL without it. We resolve the active application before
  // enabling the Pay button.
  const [activeApplicationId, setActiveApplicationId] = useState(null);
  const [paymentInFlight, setPaymentInFlight] = useState(false);

  useEffect(() => {
    if (vendorId) {
      if (paymentStatus === "success" && sessionId) {
        setLoadingPaymentStatus(true);
        handleSuccessResponse({
          session_id: sessionId,
          vendor_id: vendorId,
        });
      } else if (paymentStatus === "failed") {
        handleFailedResponse({
          session_id: sessionId,
          vendor_id: vendorId,
        });
      }
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorDetails && vendorDetails?.is_subscribed) {
      setLoading(true);

      getMySubscription(vendorId)
        .then((response) => {
          setSubscriptionData(response.data?.data);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          console.error("error", error);
          if (error && error.response) {
            message.error(
              error.response.data.message || error.response.message
            );
          } else {
            message.error("Something went wrong, please try after sometime");
          }
        });
    }
  }, [vendorId, vendorDetails]);

  // Resolve the application this payment will be applied to. Prefer the
  // application_id passed in the URL (set when the user came from the PQQ
  // page or a Stripe redirect), otherwise look up the vendor's most recent
  // recert app, otherwise the most recent app, otherwise none.
  useEffect(() => {
    if (applicationId) {
      setActiveApplicationId(applicationId);
      return;
    }
    if (!vendorId) return;
    let cancelled = false;
    ApplicationList(vendorId)
      .then((response) => {
        if (cancelled) return;
        const apps = response?.data?.applications || [];
        const next = apps.find((a) => a.recertify) || apps[0] || null;
        setActiveApplicationId(next?.application_id || null);
      })
      .catch(() => {
        if (cancelled) return;
        setActiveApplicationId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId, applicationId]);

  function handleSuccessResponse(reqBody) {
    getPaymentSuccess(reqBody)
      .then((res) => {
        setTransactionId(res.data.url.payment_intent);
        setLoadingPaymentStatus(false);
        setFormType(res.data.url.payment_status);
        if (!vendorDetails?.is_subscribed) {
          setVendorDetails({ ...vendorDetails, is_subscribed: true });
        }
        
        // If payment was for a PQQ application, redirect to certification page after a short delay
        if (applicationId && res.data.url.payment_status === "paid") {
          message.success("Payment successful! Redirecting to certification...", 2);
          setTimeout(() => {
            history.push(`/vendor/${vendorId}/vendor-profile/submit-pqq?form=${applicationId}`);
          }, 2000);
        }
      })
      .catch((error) => {
        console.error("error", error);
        if (error && error.response) {
          message.error(error.response.data.message || error.response.message);
        } else {
          message.error("get payment success error");
        }
      });
  }

  function handleFailedResponse(reqBody) {
    getPaymentFailed(reqBody)
      .then((res) => {
        setTransactionId(res.data.url.payment_intent);
        setLoadingPaymentStatus(false);
        setFormType(res.data.url.payment_status);
      })
      .catch((error) => {
        console.error("error", error);
        if (error && error.response) {
          message.error(error.response.data.message || error.response.message);
        } else {
          message.error("get payment failed error");
        }
      });
  }

  function handlePayment() {
    if (!activeApplicationId) {
      message.error(
        "We couldn't find a PQQ to apply this payment to. Please open your PQQ first or create one."
      );
      return;
    }
    setPaymentInFlight(true);
    getPaymentURL({
      vendor_id: vendorId,
      pqq: activeApplicationId,
      recertify: vendorDetails?.recertify ? "1" : "0",
    })
      .then((res) => {
        window.location.href = res.data.url;
      })
      .catch((error) => {
        setPaymentInFlight(false);
        console.error("error", error);
        if (error && error.response) {
          message.error(error.response.data.message || error.response.message);
        } else {
          message.error("get payment URL error");
        }
      });
  }

  function showModal() {
    setIsModalVisible(true);
  }
  function handleResultForm() {
    return !formType && loadingPaymentStatus
      ? {
          title: "Please wait while we are getting your payment status!",
        }
      : formType === "paid"
      ? {
          status: "success",
          title: "Payment successful",
          subTitle: applicationId 
            ? `Transaction Id: ${transactionId}. Redirecting to certification...`
            : `Transaction Id: ${transactionId}`,
          extra: applicationId ? [
            <Button 
              type="primary" 
              key="continue"
              onClick={() => {
                history.push(`/vendor/${vendorId}/vendor-profile/submit-pqq?form=${applicationId}`);
              }}
            >
              Continue to Certification
            </Button>
          ] : undefined,
        }
      : formType === "unpaid"
      ? {
          status: "error",
          title: "Sorry, Your Payment has failed!",
          extra: [
            <Button
              type="primary"
              key="retry"
              loading={paymentInFlight || !vendorId}
              disabled={!activeApplicationId}
              onClick={handlePayment}
            >
              Retry Payment
            </Button>,
            <Button
              type="primary"
              key="cancel"
              onClick={() => setFormType(null)}
            >
              Cancel
            </Button>,
          ],
        }
      : subscriptionData.length === 0
      ? {
          title: "No Payments Found",
          status: "404",
        }
      : {
          title: vendorDetails?.recertify
            ? "Annual recertification — $750.00 + 2.9% transaction fee"
            : "Vendor pre-qualification — $1,250.00 + 2.9% transaction fee",
          subTitle: vendorDetails?.recertify
            ? "Recertification renews your i4Profile for another year. PQQs remain pending until payment is received and processed."
            : "This covers the initial Pre-Qualification process. An additional $750.00 + 2.9% transaction fee is required annually for recertification. PQQs remain pending until payment is received and processed.",
          extra: [
            <Button
              type="primary"
              key="pay"
              loading={paymentInFlight || !vendorId}
              disabled={!activeApplicationId}
              onClick={handlePayment}
            >
              Pay Online
            </Button>,
            <Button
              key="offline"
              loading={!vendorId}
              disabled={!activeApplicationId}
              onClick={showModal}
            >
              Make Offline Payment
            </Button>,
          ],
        };
  }

  const columns = [
    {
      title: "Payment Made By",
      dataIndex: "paid_by",
      key: "paid_by",
      sorter: (a, b) => a.paid_by - b.paid_by,
    },
    {
      title: "Payment For PQQ",
      dataIndex: "pqq_id",
      key: "pqq_id",
      render: (text) => text || "-",
      sorter: (a, b) => {
        return a.pqq_id - b.pqq_id;
      },
    },
    {
      title: "Payment Made On",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text) => (text ? moment(text).format("MMM Do YYYY") : "-"),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: "Payment Of",
      dataIndex: "amount",
      key: "amount",
      render: (text) => text || "-",
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Mode of payment",
      dataIndex: "is_offline_transaction",
      key: "is_offline_transaction",
      render: (isOffline) => (isOffline ? "Offline" : "Online"),
      sorter: (a, b) => a.is_offline_transaction - b.is_offline_transaction,
    },
    {
      title: "Transaction ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      render: (text) => text || "-",
      sorter: (a, b) => {
        if (!a.transaction_id) return 1;
        if (!b.transaction_id) return -1;
        return a.transaction_id?.localeCompare(b.transaction_id);
      },
    },
  ];
  return (
    <Fragment>
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <CardWizz>
            {vendorDetails?.is_subscribed ? (
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Title level={3}>
                    <CheckCircleTwoTone
                      twoToneColor="#16E872"
                      style={{ fontSize: "32px" }}
                    />{" "}
                    Your payment has been received.
                  </Title>
                </Col>
                <Col span={24}>
                  <Table
                    pagination={false}
                    bordered={true}
                    dataSource={subscriptionData}
                    columns={columns}
                    loading={loading}
                  />
                </Col>
              </Row>
            ) : (
              <ResultWizz {...handleResultForm()} />
            )}
          </CardWizz>
        </>
      )}
      {isModalVisible && (
        <OfflinePayment
          vendorId={vendorId}
          applicationId={activeApplicationId}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      )}
    </Fragment>
  );
};

export default GetCerfied;
